import { ArrowLeft, Check } from 'lucide-react';
import { useState } from 'react';
import { errorMessage } from '@/api/client';
import { useAddWidgets, useConnectionKpis, useConnections, useIntegrations } from '@/api/hooks';
import type { Connection } from '@/api/types';
import { Dialog } from '@/components/Dialog';
import { useToast } from '@/components/toast-context';
import { Button, ButtonLink, Checkbox, EmptyState, ErrorNote, IntegrationIcon, Skeleton } from '@/components/ui';
import { DemoBadge } from '@/features/integrations/status';

/** Customise → Add KPIs: pick a connection, then tick the KPIs to add (GET /connections/{id}/kpis). */
export function AddKpisDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const connections = useConnections();
  const integrations = useIntegrations();
  const [connection, setConnection] = useState<Connection | null>(null);
  const kpis = useConnectionKpis(connection?.id);
  const add = useAddWidgets();
  const toast = useToast();
  const [picked, setPicked] = useState<string[]>([]);
  const nameOf = (id: string) => integrations.data?.integrations.find((i) => i.id === id)?.name ?? id;
  const usable = (connections.data ?? []).filter((c) => c.status !== 'pending');

  const close = () => {
    setConnection(null);
    setPicked([]);
    onClose();
  };

  async function submit() {
    if (!connection || !picked.length) return;
    try {
      await add.mutateAsync(picked.map((kpi_key) => ({ connection_id: connection.id, kpi_key })));
      toast({ title: `Added ${picked.length} KPI${picked.length === 1 ? '' : 's'}`, description: `${nameOf(connection.integration_id)} · ${connection.display_name}` });
      close();
    } catch {
      /* shown below */
    }
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      title={connection ? `Add from ${nameOf(connection.integration_id)}` : 'Add KPIs'}
      description={connection ? connection.display_name : 'Choose a connected app.'}
      footer={
        connection ? (
          <>
            <Button onClick={() => setConnection(null)} icon={<ArrowLeft className="size-4" aria-hidden />}>
              Back
            </Button>
            <Button variant="primary" onClick={() => void submit()} disabled={!picked.length} loading={add.isPending}>
              Add {picked.length || ''} to dashboard
            </Button>
          </>
        ) : undefined
      }
    >
      {!connection && (
        <>
          {connections.isPending && <Skeleton className="h-32" />}
          {connections.isError && <ErrorNote message={errorMessage(connections.error)} />}
          {connections.data && usable.length === 0 && (
            <EmptyState
              title="No apps connected yet"
              actions={
                <ButtonLink to="/integrations" variant="primary" onClick={close}>
                  Connect an app
                </ButtonLink>
              }
            >
              Connect an app first, then pick the numbers you want to see.
            </EmptyState>
          )}
          <ul className="flex flex-col gap-2">
            {usable.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => {
                    setConnection(c);
                    setPicked([]);
                  }}
                  className="flex w-full items-center gap-3 rounded-card border border-border bg-card p-3 text-left hover:bg-muted"
                >
                  <IntegrationIcon name={nameOf(c.integration_id)} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{nameOf(c.integration_id)}</span>
                    <span className="block truncate text-xs text-muted-ink">{c.display_name}</span>
                  </span>
                  {c.is_demo && <DemoBadge />}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      {connection && (
        <div>
          {kpis.isPending && <Skeleton className="h-40" />}
          {kpis.isError && <ErrorNote message={errorMessage(kpis.error)} onRetry={() => void kpis.refetch()} />}
          {kpis.data && (
            <fieldset className="flex flex-col gap-3">
              <legend className="sr-only">KPIs</legend>
              {kpis.data.map((k) =>
                k.on_dashboard ? (
                  <div key={k.key} className="flex items-start gap-3 text-sm">
                    <Check className="mt-0.5 size-4 text-up" aria-hidden />
                    <span>
                      <span className="font-medium">{k.label}</span>
                      <span className="block text-xs text-muted-ink">Already on your dashboard</span>
                    </span>
                  </div>
                ) : (
                  <Checkbox
                    key={k.key}
                    label={k.label}
                    description={k.description ?? (k.conditional ? 'Only available for some accounts' : undefined)}
                    checked={picked.includes(k.key)}
                    onChange={(e) => setPicked((p) => (e.target.checked ? [...p, k.key] : p.filter((x) => x !== k.key)))}
                  />
                ),
              )}
            </fieldset>
          )}
          {add.isError && (
            <div className="mt-4">
              <ErrorNote message={errorMessage(add.error)} />
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}
